import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Link2, Loader2, Pencil, ShieldAlert } from 'lucide-react';
import { toast } from './ui/sonner';
import { DriverMatchPill, DriverSearchList } from './DriverMatchCell';
import { MATCH_TYPE_LABELS } from '../lib/loadStatus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Link an imported "Unlinked" driver name to a registered driver (admin / dispatcher). */
const LinkDriverModal = ({ load, onClose, onLinked }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // {driver_id, name, match_type}
  const [busy, setBusy] = useState(false);
  const [overwriteNeeded, setOverwriteNeeded] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const originalName = data?.original_name || load?.driver_name_import_raw || load?.unmatched_driver_name || '';

  useEffect(() => {
    if (!load) return;
    setLoading(true);
    axios.get(`${API}/loads/${load.id}/driver-match-candidates`, { withCredentials: true })
      .then((r) => { setData(r.data); setNameDraft(r.data.original_name || ''); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load suggestions'))
      .finally(() => setLoading(false));
  }, [load]);

  const link = async (confirmOverwrite = false) => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/loads/${load.id}/link-driver`, { driver_id: selected.driver_id, confirm_overwrite: confirmOverwrite }, { withCredentials: true });
      toast.success(`Linked ${selected.name} to load ${load.load_ref || ''}`);
      onLinked?.(r.data);
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === 'confirm_overwrite') { setOverwriteNeeded(true); return; }
      toast.error(typeof d === 'string' ? d : d?.message || 'Could not link driver');
    } finally {
      setBusy(false);
    }
  };

  const saveName = async () => {
    if (!nameDraft.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/loads/${load.id}/unlinked-driver-name`, { driver_name: nameDraft.trim() }, { withCredentials: true });
      toast.success('Imported driver name updated');
      setEditingName(false);
      const r = await axios.get(`${API}/loads/${load.id}/driver-match-candidates`, { withCredentials: true });
      setData(r.data);
      onLinked?.(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update name');
    } finally {
      setBusy(false);
    }
  };

  if (!load) return null;
  const suggested = data?.suggested || [];

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4" data-testid="link-driver-modal" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-md shadow-2xl flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Link driver to load {load.load_ref || load.id?.slice(-6)}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{load.pickup_location} → {load.delivery_location}</p>
          </div>
          <button data-testid="link-driver-close" onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Imported driver name</p>
            {editingName ? (
              <div className="flex items-center gap-2 mt-1">
                <input data-testid="link-driver-name-input" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} className="flex-1 px-2 py-1.5 border border-slate-300 rounded text-sm" />
                <button data-testid="link-driver-name-save" onClick={saveName} disabled={busy} className="px-3 py-1.5 text-xs font-semibold bg-[#002FA7] text-white rounded">Save</button>
                <button onClick={() => setEditingName(false)} className="px-2 py-1.5 text-xs text-slate-600">Cancel</button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2 mt-1">
                <p className="text-base font-semibold text-slate-900" data-testid="link-driver-original-name">{originalName || <em className="text-slate-400">none</em>}</p>
                <div className="flex items-center gap-2">
                  <DriverMatchPill status={data?.status || 'unlinked'} />
                  <button data-testid="link-driver-edit-name" onClick={() => setEditingName(true)} className="text-xs text-[#002FA7] font-semibold inline-flex items-center gap-1 hover:underline"><Pencil className="w-3 h-3" /> Edit</button>
                </div>
              </div>
            )}
            <p className="text-[11px] text-slate-500 mt-1">The original name stays on the load’s history after linking.</p>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Finding matches…</div>
          ) : (
            <>
              {suggested.length > 0 && (
                <div data-testid="link-driver-suggestions">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">
                    Suggested {data?.match_type ? `· ${MATCH_TYPE_LABELS[data.match_type] || data.match_type}` : ''}
                  </p>
                  <div className="space-y-1.5">
                    {suggested.map((c) => (
                      <button key={c.driver_id} type="button" data-testid={`link-driver-suggest-${c.driver_id}`}
                        onClick={() => { setSelected({ ...c, match_type: data.match_type }); setOverwriteNeeded(false); }}
                        className={`w-full text-left px-3 py-2 rounded-md border text-sm ${selected?.driver_id === c.driver_id ? 'border-[#002FA7] bg-[#002FA7]/5' : 'border-slate-200 hover:border-slate-400'}`}>
                        <span className="font-semibold text-slate-900">{c.name}</span>
                        {c.email && <span className="text-slate-500 text-xs"> · {c.email}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Or search all registered drivers</p>
                <DriverSearchList drivers={data?.drivers || []} onPick={(d) => { setSelected({ ...d, match_type: 'manual' }); setOverwriteNeeded(false); }} testIdPrefix="link-driver-search" />
              </div>
            </>
          )}

          {selected && (
            <div className="border border-emerald-300 bg-emerald-50 rounded-md p-3" data-testid="link-driver-confirm-box">
              <p className="text-sm text-emerald-900">
                Link <b>{selected.name}</b> to this load? “{originalName}” will be kept as the imported name in the assignment history.
              </p>
              {overwriteNeeded && (
                <p className="mt-2 text-xs text-red-800 flex items-start gap-1.5" data-testid="link-driver-overwrite-warning">
                  <ShieldAlert className="w-4 h-4 flex-shrink-0" /> This load already has a confirmed driver. Linking will replace that assignment — confirm again to proceed.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900">Cancel</button>
          <button
            data-testid="link-driver-confirm"
            disabled={!selected || busy}
            onClick={() => link(overwriteNeeded)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-[#002FA7] text-white rounded-md hover:bg-[#00247D] disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            {overwriteNeeded ? 'Confirm replace & link' : 'Confirm link'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkDriverModal;
